import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { flag } from "../lib/flag";
import { useNation } from "../lib/useNation";
import { NationSwitcher } from "./NationSwitcher";
import { GlobalSearch } from "./GlobalSearch";
import { AccountMenu } from "./AccountMenu";
import { LanguagePicker } from "./LanguagePicker";
import { useT } from "../i18n";

const TABS = [
  { to: "/", key: "nav.home", end: true, icon: "🏛" },
  { to: "/world", key: "nav.world", icon: "🌍" },
  { to: "/polls", key: "nav.polls", icon: "🗳" },
  { to: "/candidates", key: "nav.candidates", icon: "📜" },
] as const;

// Persistent top nav: crest wordmark, your-nation chip + ⇄ switcher, section
// tabs, global search. Sticks just below the 30px ticker.
export function Nav() {
  const t = useT();
  const { nation } = useNation();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Admin is a chrome-free moderation tool — no public nation chrome there.
  if (useLocation().pathname.startsWith("/admin")) return null;

  return (
    <>
    <header className="sticky top-0 z-30 border-b border-border bg-bg/80 backdrop-blur">
      <div className="max-w-[1120px] mx-auto px-5 h-14 flex items-center gap-2">
        <Link to="/" className="flex items-center gap-2.5 sm:mr-2 shrink-0">
          <img src="/favicon.svg" alt="" width={30} height={30} className="shrink-0" />
          <span className="leading-none hidden sm:block">
            <span className="block font-blk text-[15px] tracking-wide">WarUrna</span>
            <span className="block font-mono text-[9px] text-accent tracking-[0.18em] uppercase">election forecasts</span>
          </span>
        </Link>

        {nation ? (
          <span className="flex items-center gap-1.5 bg-panel border border-border rounded-full pl-2.5 pr-1.5 py-1 text-sm min-w-0">
            <Link to={`/c/${nation.id}`} className="flex items-center gap-1.5 min-w-0 max-w-[8rem] truncate font-disp font-bold">
              <span className="shrink-0">{flag(nation.code)}</span>
              <span className="truncate">{nation.name}</span>
            </Link>
            <button type="button" onClick={() => setSwitcherOpen(true)} title={t("nav.changeNation")} className="shrink-0 grid place-items-center w-6 h-6 leading-none font-mono text-xs text-dim hover:text-accent hover:border-accent border border-border rounded-md transition">
              ⇄
            </button>
          </span>
        ) : (
          <button type="button" onClick={() => setSwitcherOpen(true)} className="chip on shrink-0">
            ★ {t("nav.pickNation")}
          </button>
        )}

        <nav className="hidden sm:flex items-center gap-0.5 ml-2">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={"end" in tab ? tab.end : undefined}
              className={({ isActive }) =>
                `font-disp font-bold text-[13px] tracking-wide uppercase px-3 py-2 rounded-lg transition ${
                  isActive ? "bg-accent text-[#1a0e00]" : "text-muted hover:text-ink hover:bg-panel"
                }`
              }
            >
              {t(tab.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="flex items-center gap-2 bg-panel border border-border rounded-lg px-3 py-1.5 text-dim font-mono text-xs hover:border-accent transition"
        >
          <span>🔍</span>
          <span className="hidden sm:inline">{t("nav.search")}</span>
        </button>

        <LanguagePicker />
        <AccountMenu />
      </div>

      {switcherOpen && <NationSwitcher onClose={() => setSwitcherOpen(false)} />}
      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
    </header>

      {/* Mobile section nav — the top-row tabs are hidden < sm, so phones reach
          World/Polls/Candidates from this fixed bottom bar. Fits inside Page's
          pb-24 so it never covers content. */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-30 flex border-t border-border bg-bg/90 backdrop-blur">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={"end" in tab ? tab.end : undefined}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-0.5 py-2 font-disp font-bold text-[10px] tracking-wide uppercase transition ${
                isActive ? "text-accent" : "text-muted"
              }`
            }
          >
            <span className="text-base leading-none">{tab.icon}</span>
            {t(tab.key)}
          </NavLink>
        ))}
      </nav>
    </>
  );
}
